import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { setPageMeta, fmtDate, mdToHtml } from '../lib/shop';

export default function JournalArticle() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const { user, refresh } = useAuth();

  useEffect(() => {
    setA(null);
    api.get(`/journal/${slug}`).then((r) => {
      setA(r.data);
      setPageMeta(r.data.seo_title || r.data.title, r.data.seo_description || r.data.excerpt);
    }).catch(() => setA(false));
  }, [slug]);

  if (a === null) return <div className="max-w-3xl mx-auto px-4 py-20"><div className="h-64 bg-[#F4F4F0] animate-pulse" /></div>;
  if (a === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-center font-display font-bold uppercase text-2xl">Article not found</div>;

  const saveArticle = async () => {
    if (!user) { toast.error('Sign in to save articles'); return; }
    const saved = new Set(user.saved_articles || []);
    saved.has(a.id) ? saved.delete(a.id) : saved.add(a.id);
    await api.put('/auth/profile', { name: user.name, addresses: user.addresses || [], saved_products: user.saved_products || [], saved_articles: [...saved] });
    await refresh();
    toast.success(saved.has(a.id) ? 'Article saved' : 'Removed from saved articles');
  };

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="article-page">
      <nav className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95] mb-6">
        <Link to="/journal" className="hover:text-[#0B0F12]">Research Journal</Link> / <span className="text-[#0B0F12]">{a.category}</span>
      </nav>
      <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]">{a.title}</h1>
      <div className="flex items-center justify-between mt-5 pb-5 border-b hairline">
        <p className="font-mono2 text-[11px] uppercase tracking-widest text-[#8A8F95]">{a.author} · {fmtDate(a.publish_at)} · {a.reading_time} min read</p>
        <button data-testid="save-article-button" onClick={saveArticle} className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-widest text-[#575B60] hover:text-[#0B0F12]">
          <Bookmark className="w-4 h-4" /> Save
        </button>
      </div>
      {a.hero_image && <img src={a.hero_image} alt="" className="w-full aspect-[16/8] object-cover mt-8 border hairline" />}
      <div className="vq-prose mt-8" dangerouslySetInnerHTML={{ __html: mdToHtml(a.content) }} />
      {(a.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-10">
          {a.tags.map((t) => <span key={t} className="border hairline px-3 py-1 font-mono2 text-[10px] uppercase tracking-wider text-[#575B60]">#{t}</span>)}
        </div>
      )}
      <div className="mt-10 bg-[#FFF7ED] border border-[#FDE68A] p-4 text-[13px] text-[#575B60] leading-relaxed">
        Journal content is educational material about laboratory research practice. It is not medical advice and does not describe or endorse any human or veterinary use.
      </div>
      {(a.related || []).length > 0 && (
        <div className="mt-12">
          <h2 className="font-display font-black uppercase text-2xl mb-5">Related Articles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {a.related.map((r) => (
              <Link key={r.id} to={`/journal/${r.slug}`} className="border hairline bg-white p-5 hover:border-[#0B0F12] transition-colors">
                <p className="font-mono2 text-[9px] uppercase tracking-[0.18em] text-[#8A8F95]">{r.category}</p>
                <p className="font-display font-bold uppercase text-base leading-tight mt-2">{r.title}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
